import { FC, useState, useEffect, useMemo } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CodeIcon from "@mui/icons-material/Code";
import DescriptionIcon from "@mui/icons-material/Description";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import Collapse from "@mui/material/Collapse";
import Tooltip from "@mui/material/Tooltip";
import DOMPurify from "dompurify";
import {
	useCreateTreatmentPlan,
	useUpdateTreatmentPlan,
} from "../../hooks/useTreatmentPlans";
import { useRewardsEngine } from "../../hooks/useRewardsEngine";
import { useCups } from "../../hooks/useCups";
import {
	TreatmentPlanRequest,
	TreatmentPlan,
	TreatmentConfigTemplate,
	CmVisitScheduleOption,
	ProgramType,
	RewardEngineOption,
	TreatmentSegment,
} from "../../domain/treatmentPlan.interface";
import ProgramTypeSelector from "./components/ProgramTypeSelector";
import FieldDefinitionsEditor from "./components/FieldDefinitionsEditor";
import SmartProgramConfig from "./components/SmartProgramConfig";
import { GenerateSmartProgramResponse } from "../../services/treatmentPlan.service";
import {
	SmartProgramWorkflow,
	FieldDefinition,
} from "../../domain/treatmentPlan.interface";
import { Event } from "../../domain/event.interface";
import BehaviorSelector from "../../components/BehaviorSelector";
import TreatmentConfigFields from "../../components/TreatmentConfigFields";
import SegmentEditor from "../../components/SegmentEditor";
import {
	contiguityErrorMessage,
	readPlanSegments,
	validateSegmentOverrides,
	validateSegmentStructure,
} from "../../components/SegmentEditor/segmentEditor.helpers";
import {
	COMMON_VISIT_SCHEDULES,
	WEEKDAYS,
	formatWeekdaysShort,
	formatWeekdaysDisplay,
} from "../../utils/temporal.utils";
import { useUserStore } from "../../stores/user.store";

const CopyToClipboardButton: FC<{ text: string }> = ({ text }) => {
	const [copied, setCopied] = useState(false);
	return (
		<Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
			<IconButton
				size="small"
				onClick={() => {
					navigator.clipboard.writeText(text);
					setCopied(true);
					setTimeout(() => setCopied(false), 2000);
				}}
			>
				{copied ? (
					<CheckIcon fontSize="small" color="success" />
				) : (
					<ContentCopyIcon fontSize="small" />
				)}
			</IconButton>
		</Tooltip>
	);
};

const SavedSmartProgramDetails: FC<{
	workflow: SmartProgramWorkflow;
	fieldDefinitions: FieldDefinition[];
	description?: string;
	generationPrompt?: string;
	previewHtml?: string;
}> = ({
	workflow,
	fieldDefinitions,
	description,
	generationPrompt,
	previewHtml,
}) => {
	const [showJson, setShowJson] = useState(false);
	const [showPrompt, setShowPrompt] = useState(false);
	const [showPreview, setShowPreview] = useState(false);

	const jsonContent = JSON.stringify({ workflow, fieldDefinitions }, null, 2);

	// Extract the auto-generated part (event catalog, instructions) by removing the user description portion
	const autoGeneratedContext =
		generationPrompt && description
			? generationPrompt.substring(
					generationPrompt.indexOf(`"${description}"`) + description.length + 3,
				)
			: "";

	return (
		<>
			<Grid item xs={12}>
				<Box sx={{ display: "flex", gap: 1 }}>
					<Button
						size="small"
						variant={showJson ? "contained" : "outlined"}
						startIcon={<CodeIcon />}
						onClick={() => setShowJson(!showJson)}
					>
						JSON
					</Button>
					{generationPrompt && (
						<Button
							size="small"
							variant={showPrompt ? "contained" : "outlined"}
							startIcon={<DescriptionIcon />}
							onClick={() => setShowPrompt(!showPrompt)}
						>
							Prompt
						</Button>
					)}
					{previewHtml && (
						<Button
							size="small"
							variant={showPreview ? "contained" : "outlined"}
							startIcon={<AutoAwesomeIcon />}
							onClick={() => setShowPreview(!showPreview)}
						>
							Preview
						</Button>
					)}
				</Box>
			</Grid>
			<Grid item xs={12}>
				<Collapse in={showJson}>
					<Paper variant="outlined" sx={{ mb: 1 }}>
						<Box
							sx={{
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								px: 2,
								py: 1,
								borderBottom: "1px solid #e0e0e0",
								bgcolor: "#f5f5f5",
							}}
						>
							<Typography variant="caption" color="text.secondary">
								Workflow JSON
							</Typography>
							<CopyToClipboardButton text={jsonContent} />
						</Box>
						<Box
							component="pre"
							sx={{
								p: 2,
								m: 0,
								overflow: "auto",
								maxHeight: 400,
								fontSize: 12,
								fontFamily: "monospace",
							}}
						>
							{jsonContent}
						</Box>
					</Paper>
				</Collapse>
				<Collapse in={showPrompt}>
					{description && (
						<Paper variant="outlined" sx={{ mb: 1 }}>
							<Box
								sx={{
									display: "flex",
									justifyContent: "space-between",
									alignItems: "center",
									px: 2,
									py: 1,
									borderBottom: "1px solid #e0e0e0",
									bgcolor: "#f5f5f5",
								}}
							>
								<Typography variant="caption" color="text.secondary">
									User Description
								</Typography>
								<CopyToClipboardButton text={description} />
							</Box>
							<Box
								component="pre"
								sx={{
									p: 2,
									m: 0,
									overflow: "auto",
									maxHeight: 200,
									fontSize: 12,
									fontFamily: "monospace",
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
								}}
							>
								{description}
							</Box>
						</Paper>
					)}
					{autoGeneratedContext && (
						<Paper variant="outlined" sx={{ mb: 1, opacity: 0.7 }}>
							<Box
								sx={{
									px: 2,
									py: 1,
									borderBottom: "1px solid #e0e0e0",
									bgcolor: "#f5f5f5",
								}}
							>
								<Typography variant="caption" color="text.secondary">
									Auto-generated Context (events catalog, instructions)
								</Typography>
							</Box>
							<Box
								component="pre"
								sx={{
									p: 2,
									m: 0,
									overflow: "auto",
									maxHeight: 200,
									fontSize: 11,
									fontFamily: "monospace",
									whiteSpace: "pre-wrap",
									wordBreak: "break-word",
									color: "text.secondary",
								}}
							>
								{autoGeneratedContext}
							</Box>
						</Paper>
					)}
				</Collapse>
				{previewHtml && (
					<Collapse in={showPreview}>
						<Paper
							variant="outlined"
							sx={{
								p: 2,
								mb: 1,
								overflow: "auto",
								"& > div": { maxWidth: "100%" },
								"& h1, & h2, & h3, & h4": { margin: "8px 0" },
								"& p": { margin: "4px 0" },
								"& table": {
									width: "100%",
									borderCollapse: "collapse",
									"& th, & td": {
										border: "1px solid #e0e0e0",
										padding: "8px",
										textAlign: "left",
									},
									"& th": { backgroundColor: "#f5f5f5" },
								},
							}}
							dangerouslySetInnerHTML={{
								__html: DOMPurify.sanitize(previewHtml, {
									ALLOWED_TAGS: [
										"div",
										"span",
										"p",
										"h1",
										"h2",
										"h3",
										"h4",
										"h5",
										"h6",
										"ul",
										"ol",
										"li",
										"table",
										"thead",
										"tbody",
										"tr",
										"th",
										"td",
										"strong",
										"em",
										"br",
										"hr",
										"code",
										"pre",
									],
									ALLOWED_ATTR: ["class", "style"],
								}),
							}}
						/>
					</Collapse>
				)}
			</Grid>
		</>
	);
};

interface TreatmentPlanFormProps {
	editingPlan: TreatmentPlan | null;
	onClose: () => void;
	events?: Event[];
	eventsLoading: boolean;
}

const TreatmentPlanForm: FC<TreatmentPlanFormProps> = ({
	editingPlan,
	onClose,
	events,
	eventsLoading,
}) => {
	const { userInfo } = useUserStore();
	const defaultAccountId =
		process.env.REACT_APP_ACCOUNT_ID || "507f1f77bcf86cd799439011";

	const isEditMode = !!editingPlan && !!editingPlan._id;

	const [formData, setFormData] = useState<TreatmentPlanRequest>({
		accountId: defaultAccountId,
		name: "",
		programType: "WEEKLY_CALENDAR",
		treatmentConfig: {},
		treatmentConfigOptions: {
			treatmentConfigTemplates: [],
			visitsPerPeriod: [],
			minHoursBetweenVisits: [],
			duration: [],
			behavior: [],
			cmVisitScheduleOptions: [],
		},
		customPlanAllowed: false,
		// SIMS-8910 AC4: a new plan generates missed entries. The control maps 1:1 onto the
		// stored negative flag, so "defaults to generating" is the same statement as
		// "defaults to false" — there is no inversion anywhere that could drift.
		disableMissedEntriesGeneration: false,
	});

	const [newVisitPeriod, setNewVisitPeriod] = useState("");
	const [newMinHours, setNewMinHours] = useState("");
	const [newDuration, setNewDuration] = useState("");

	// Template creation state
	const [showTemplateForm, setShowTemplateForm] = useState(false);
	const [editingTemplateIndex, setEditingTemplateIndex] = useState<
		number | null
	>(null);
	const [templateData, setTemplateData] = useState<TreatmentConfigTemplate>({
		name: "",
		default: false,
		segment: 0,
		range: "",
		duration: 0,
		periodLength: 1,
		weeklyVisitsPerPeriod: 0,
		cmVisitScheduleOptionIds: "",
		rewardEngineId: "",
		visitsPerPeriod: 0,
		minHoursBetweenVisits: 0,
		cmVisitSchedule: {
			cmVisitScheduleOptions: 0,
			cmVisitScheduleOptionsDays: "",
			cmVisitScheduleOptionsDaysDisplay: "",
			cutDays: [],
		},
		behavior: "",
		disenrollmentDaysThreshold: undefined,
		allowCustomProviderSite: false,
		additionalTreatmentConfigs: [],
	});

	// CM Visit Schedule Options creation state
	const [showCMVisitScheduleForm, setShowCMVisitScheduleForm] = useState(false);
	const [cmVisitScheduleData, setCMVisitScheduleData] = useState<
		CmVisitScheduleOption & {
			selectedWeekdays: number[];
			selectedCutDays: number[];
		}
	>({
		cmVisitScheduleOptions: 0,
		cmVisitScheduleOptionsDays: "",
		cmVisitScheduleOptionsDaysDisplay: "",
		cutDays: [],
		selectedWeekdays: [],
		selectedCutDays: [],
	});

	// State for adding new cut day patterns
	const [showCutDayForm, setShowCutDayForm] = useState(false);
	const [newCutDayPattern, setNewCutDayPattern] = useState({
		runningday: "Mon",
		startcutday: "Mon",
		endcutday: "Mon",
	});

	const { data: rewardEngines } = useRewardsEngine(true);
	const { data: cupsData } = useCups();
	const createMutation = useCreateTreatmentPlan();
	const updateMutation = useUpdateTreatmentPlan();

	// SIMS-7151: reward engine id -> display name options, reused by the segment editor's
	// collapsed header (mirrors the per-template mapping below).
	const rewardEngineOptions: RewardEngineOption[] = useMemo(
		() =>
			rewardEngines
				?.filter((engine) => engine._id)
				.map((engine) => ({
					id: engine._id as string,
					name: `${engine.eventType} - ${engine.event}`,
				})) || [],
		[rewardEngines],
	);

	// SIMS-7151/7159: the plan's default config template supplies the effective values a segment
	// inherits when its override toggle is OFF (reward engine, behavior, visit schedule,
	// disenrollment threshold).
	const defaultTemplate =
		formData.treatmentConfigOptions?.treatmentConfigTemplates?.find(
			(t) => t.default,
		) || formData.treatmentConfigOptions?.treatmentConfigTemplates?.[0];
	const segmentTemplateDefaults = {
		rewardEngineId: defaultTemplate?.rewardEngineId,
		behavior: defaultTemplate?.behavior,
		cmVisitScheduleOptionIds: defaultTemplate?.cmVisitScheduleOptionIds,
		disenrollmentDaysThreshold: defaultTemplate?.disenrollmentDaysThreshold,
	};

	// Load editing plan data
	useEffect(() => {
		if (editingPlan) {
			setFormData({
				accountId: editingPlan.accountId,
				name: editingPlan.name || "",
				programType: editingPlan.programType || "WEEKLY_CALENDAR",
				treatmentConfig: editingPlan.treatmentConfig || {},
				treatmentConfigOptions: editingPlan.treatmentConfigOptions || {
					treatmentConfigTemplates: [],
					visitsPerPeriod: [],
					minHoursBetweenVisits: [],
					duration: [],
					behavior: [],
					cmVisitScheduleOptions: [],
				},
				smartProgram: editingPlan.smartProgram,
				customPlanAllowed: editingPlan.customPlanAllowed || false,
				// SIMS-8910 AC1/AC2: reopening the plan shows the saved state. A plan saved
				// before this release has no value here and reads as generating.
				disableMissedEntriesGeneration:
					editingPlan.disableMissedEntriesGeneration ?? false,
				// SIMS-7151: pre-populate segments from the folded read location
				// (treatmentConfig.segments); absent/empty yields [] (AC3/AC4).
				segments: readPlanSegments(editingPlan.treatmentConfig),
			});
		}
	}, [editingPlan]);

	const handleSubmit = async () => {
		try {
			// Clean the payload before sending to API
			const cleanedFormData = { ...formData };

			// Remove frontend-only fields from cmVisitScheduleOptions
			if (cleanedFormData.treatmentConfigOptions?.cmVisitScheduleOptions) {
				cleanedFormData.treatmentConfigOptions.cmVisitScheduleOptions =
					cleanedFormData.treatmentConfigOptions.cmVisitScheduleOptions.map(
						(option) => {
							const cleanOption = { ...option };
							if ("selectedWeekdays" in cleanOption) {
								delete (cleanOption as Record<string, unknown>)
									.selectedWeekdays;
							}
							if ("selectedCutDays" in cleanOption) {
								delete (cleanOption as Record<string, unknown>).selectedCutDays;
							}
							return cleanOption;
						},
					);
			}

			// Remove frontend-only fields from cmVisitSchedule in templates
			if (cleanedFormData.treatmentConfigOptions?.treatmentConfigTemplates) {
				cleanedFormData.treatmentConfigOptions.treatmentConfigTemplates =
					cleanedFormData.treatmentConfigOptions.treatmentConfigTemplates.map(
						(template) => {
							const cleanTemplate = { ...template };
							if (cleanTemplate.cmVisitSchedule) {
								const cleanSchedule = { ...cleanTemplate.cmVisitSchedule };
								if ("selectedWeekdays" in cleanSchedule) {
									delete (cleanSchedule as Record<string, unknown>)
										.selectedWeekdays;
								}
								if ("selectedCutDays" in cleanSchedule) {
									delete (cleanSchedule as Record<string, unknown>)
										.selectedCutDays;
								}
								cleanTemplate.cmVisitSchedule = cleanSchedule;
							}
							return cleanTemplate;
						},
					);
			}

			// SIMS-8910 AC3 (C17850): a Smart Program plan must carry no missed-entry
			// generation value at all. The control is not rendered for that program type, but
			// the field still sits in form state at its default — and switching Program Type
			// mid-edit (C17849) can leave a value the user set while the form was weekly.
			// Dropping it here is what makes "not offered" and "not stored" the same thing,
			// rather than relying on the default happening to be harmless.
			if (cleanedFormData.programType === "SMART_PROGRAM") {
				delete cleanedFormData.disableMissedEntriesGeneration;
			}

			// Validate templates
			if (cleanedFormData.treatmentConfigOptions?.treatmentConfigTemplates) {
				const invalidTemplates =
					cleanedFormData.treatmentConfigOptions.treatmentConfigTemplates.filter(
						(template) =>
							!template.name.trim() ||
							!template.range.trim() ||
							template.segment <= 0 ||
							template.duration < 0 ||
							!template.weeklyVisitsPerPeriod ||
							!template.rewardEngineId.trim() ||
							!template.cmVisitScheduleOptionIds.trim() ||
							!template.cmVisitSchedule.cmVisitScheduleOptionsDays.trim() ||
							!template.cmVisitSchedule.cmVisitScheduleOptionsDaysDisplay.trim() ||
							!template.behavior.trim(),
					);

				if (invalidTemplates.length > 0) {
					alert(
						"Please fill all required template fields before saving the treatment plan.",
					);
					return;
				}
			}

			// SIMS-7151 (AC1): the structural fields must be usable. Checked FIRST — a segment with
			// no visits per week makes every later rule meaningless (it occupies zero visits, so it
			// silently never applies at runtime).
			const structureError = validateSegmentStructure(formData.segments || []);
			if (structureError) {
				alert(structureError);
				return;
			}

			// SIMS-7159 (AC2): a segment override toggled ON but left empty/invalid blocks save.
			const overrideError = validateSegmentOverrides(formData.segments || []);
			if (overrideError) {
				alert(overrideError);
				return;
			}

			// SIMS-7167 (AC2/AC3/AC5): non-contiguous / overlapping / not-starting-at-1 segment
			// week ranges block save (mirrors the server-side rule so the UI catches it first).
			const contiguityError = contiguityErrorMessage(formData.segments || []);
			if (contiguityError) {
				alert(contiguityError);
				return;
			}

			if (isEditMode && editingPlan) {
				await updateMutation.mutateAsync({
					id: editingPlan._id,
					data: { ...cleanedFormData, updatedBy: userInfo?.id },
				});
			} else {
				await createMutation.mutateAsync({
					...cleanedFormData,
					createdBy: userInfo?.id,
				});
			}

			onClose();
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error("Error saving treatment plan:", err);
		}
	};

	const addArrayItem = (
		field: keyof NonNullable<typeof formData.treatmentConfigOptions>,
		value: string,
		setter: (value: string) => void,
	) => {
		if (value.trim() && formData.treatmentConfigOptions) {
			const currentArray =
				(formData.treatmentConfigOptions[field] as string[]) || [];
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					[field]: [...currentArray, value.trim()],
				},
			});
			setter("");
		}
	};

	const removeArrayItem = (
		field: keyof NonNullable<typeof formData.treatmentConfigOptions>,
		index: number,
	) => {
		if (formData.treatmentConfigOptions) {
			const currentArray =
				(formData.treatmentConfigOptions[field] as string[]) || [];
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					[field]: currentArray.filter((_, i) => i !== index),
				},
			});
		}
	};

	const addTemplate = () => {
		if (!formData.treatmentConfigOptions) {
			setFormData({
				...formData,
				treatmentConfigOptions: {
					treatmentConfigTemplates: [templateData],
					visitsPerPeriod: [],
					minHoursBetweenVisits: [],
					duration: [],
					behavior: [],
				},
			});
		} else {
			const currentTemplates =
				formData.treatmentConfigOptions.treatmentConfigTemplates || [];

			// Check if we're editing an existing template
			if (editingTemplateIndex !== null) {
				// Update existing template
				const updatedTemplates = [...currentTemplates];
				updatedTemplates[editingTemplateIndex] = templateData;
				setFormData({
					...formData,
					treatmentConfigOptions: {
						...formData.treatmentConfigOptions,
						treatmentConfigTemplates: updatedTemplates,
					},
				});
			} else {
				// Add new template
				setFormData({
					...formData,
					treatmentConfigOptions: {
						...formData.treatmentConfigOptions,
						treatmentConfigTemplates: [...currentTemplates, templateData],
					},
				});
			}
		}

		// Reset form
		setTemplateData({
			name: "",
			default: false,
			segment: 0,
			range: "",
			duration: 0,
			periodLength: 1,
			weeklyVisitsPerPeriod: 0,
			cmVisitScheduleOptionIds: "",
			rewardEngineId: "",
			visitsPerPeriod: 0,
			minHoursBetweenVisits: 0,
			cmVisitSchedule: {
				cmVisitScheduleOptions: 0,
				cmVisitScheduleOptionsDays: "",
				cmVisitScheduleOptionsDaysDisplay: "",
				cutDays: [],
			},
			behavior: "",
			disenrollmentDaysThreshold: undefined,
			allowCustomProviderSite: false,
			additionalTreatmentConfigs: [],
		});
		setShowTemplateForm(false);
		setEditingTemplateIndex(null);
	};

	const removeTemplate = (index: number) => {
		if (formData.treatmentConfigOptions?.treatmentConfigTemplates) {
			const currentTemplates =
				formData.treatmentConfigOptions.treatmentConfigTemplates;
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					treatmentConfigTemplates: currentTemplates.filter(
						(_, i) => i !== index,
					),
				},
			});
		}
	};

	const editTemplate = (index: number) => {
		if (formData.treatmentConfigOptions?.treatmentConfigTemplates) {
			const template =
				formData.treatmentConfigOptions.treatmentConfigTemplates[index];
			setTemplateData(template);
			setEditingTemplateIndex(index);
			setShowTemplateForm(true);
		}
	};

	const addCMVisitScheduleOption = () => {
		const cleanedOption: CmVisitScheduleOption = {
			cmVisitScheduleOptions: cmVisitScheduleData.cmVisitScheduleOptions,
			cmVisitScheduleOptionsDays:
				cmVisitScheduleData.cmVisitScheduleOptionsDays,
			cmVisitScheduleOptionsDaysDisplay:
				cmVisitScheduleData.cmVisitScheduleOptionsDaysDisplay,
			cutDays: cmVisitScheduleData.cutDays,
		};

		if (!formData.treatmentConfigOptions) {
			setFormData({
				...formData,
				treatmentConfigOptions: {
					treatmentConfigTemplates: [],
					visitsPerPeriod: [],
					minHoursBetweenVisits: [],
					duration: [],
					behavior: [],
					cmVisitScheduleOptions: [cleanedOption],
				},
			});
		} else {
			const currentOptions =
				formData.treatmentConfigOptions.cmVisitScheduleOptions || [];
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					cmVisitScheduleOptions: [...currentOptions, cleanedOption],
				},
			});
		}

		setCMVisitScheduleData({
			cmVisitScheduleOptions: 0,
			cmVisitScheduleOptionsDays: "",
			cmVisitScheduleOptionsDaysDisplay: "",
			cutDays: [],
			selectedWeekdays: [],
			selectedCutDays: [],
		});
		setShowCMVisitScheduleForm(false);
	};

	const removeCMVisitScheduleOption = (index: number) => {
		if (formData.treatmentConfigOptions?.cmVisitScheduleOptions) {
			const currentOptions =
				formData.treatmentConfigOptions.cmVisitScheduleOptions;
			setFormData({
				...formData,
				treatmentConfigOptions: {
					...formData.treatmentConfigOptions,
					cmVisitScheduleOptions: currentOptions.filter((_, i) => i !== index),
				},
			});
		}
	};

	const handleWeekdayToggle = (weekdayValue: number) => {
		const currentWeekdays = cmVisitScheduleData.selectedWeekdays;
		const isSelected = currentWeekdays.includes(weekdayValue);
		const newWeekdays = isSelected
			? currentWeekdays.filter((day) => day !== weekdayValue)
			: [...currentWeekdays, weekdayValue].sort((a, b) => a - b);

		const daysString = formatWeekdaysShort(newWeekdays);
		const displayString = formatWeekdaysDisplay(newWeekdays, "full");

		setCMVisitScheduleData({
			...cmVisitScheduleData,
			selectedWeekdays: newWeekdays,
			cmVisitScheduleOptionsDays: daysString,
			cmVisitScheduleOptionsDaysDisplay: displayString,
		});
	};

	const handleCutDayToggle = (weekdayValue: number) => {
		const currentCutDays = cmVisitScheduleData.selectedCutDays;
		const isSelected = currentCutDays.includes(weekdayValue);
		const newCutDays = isSelected
			? currentCutDays.filter((day) => day !== weekdayValue)
			: [...currentCutDays, weekdayValue].sort((a, b) => a - b);

		const cutDaysArray = newCutDays.map((day) => ({
			runningday: formatWeekdaysShort([day]),
			startcutday: formatWeekdaysShort([day]),
			endcutday: formatWeekdaysShort([day]),
		}));

		setCMVisitScheduleData({
			...cmVisitScheduleData,
			selectedCutDays: newCutDays,
			cutDays: cutDaysArray,
		});
	};

	const handlePresetSchedule = (presetIndex: number) => {
		if (presetIndex === -1) {
			setCMVisitScheduleData({
				...cmVisitScheduleData,
				selectedWeekdays: [],
				selectedCutDays: [],
				cmVisitScheduleOptionsDays: "",
				cmVisitScheduleOptionsDaysDisplay: "",
			});
			return;
		}

		const preset = COMMON_VISIT_SCHEDULES[presetIndex];
		if (preset) {
			setCMVisitScheduleData({
				...cmVisitScheduleData,
				selectedWeekdays: preset.weekdays,
				selectedCutDays: [],
				cmVisitScheduleOptionsDays: preset.shortCode,
				cmVisitScheduleOptionsDaysDisplay: preset.name,
				cutDays: [],
			});
		}
	};

	return (
		<Card sx={{ mb: 3 }}>
			<CardContent>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 2,
					}}
				>
					<Typography variant="h6">
						{isEditMode
							? "Edit Treatment Plan"
							: editingPlan
								? "Clone Treatment Plan"
								: "Create New Treatment Plan"}
					</Typography>
					<Button variant="outlined" onClick={onClose}>
						Cancel
					</Button>
				</Box>

				<Grid container spacing={3}>
					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Account ID"
							value={formData.accountId}
							onChange={(e) =>
								setFormData({
									...formData,
									accountId: e.target.value,
								})
							}
							placeholder="Enter account ID"
							helperText="Default account ID is pre-filled from environment"
						/>
					</Grid>

					<Grid item xs={12}>
						<TextField
							fullWidth
							label="Treatment Plan Name"
							value={formData.name}
							onChange={(e) =>
								setFormData({
									...formData,
									name: e.target.value,
								})
							}
							placeholder="Enter treatment plan name"
							required
							helperText="A descriptive name to identify this treatment plan"
						/>
					</Grid>

					<Grid item xs={12}>
						<ProgramTypeSelector
							value={formData.programType || "WEEKLY_CALENDAR"}
							onChange={(type: ProgramType) =>
								setFormData({ ...formData, programType: type })
							}
							disabled={isEditMode}
						/>
					</Grid>

					{formData.programType === "SMART_PROGRAM" && (
						<>
							<Grid item xs={12}>
								{isEditMode && editingPlan ? (
									<SmartProgramConfig
										treatmentPlanId={editingPlan._id}
										onApprove={(result: GenerateSmartProgramResponse) => {
											setFormData({
												...formData,
												smartProgram: {
													workflow:
														result.workflow as unknown as SmartProgramWorkflow,
													fieldDefinitions:
														result.fieldDefinitions as FieldDefinition[],
													description: result.description,
													generationPrompt: result.prompt,
													previewHtml: result.previewHtml,
												},
											});
										}}
										onCancel={() => {
											/* no-op */
										}}
									/>
								) : (
									<Typography
										variant="body2"
										color="text.secondary"
										sx={{
											p: 2,
											textAlign: "center",
											border: "1px dashed #ccc",
											borderRadius: 1,
										}}
									>
										Save the treatment plan first, then use AI generation to
										configure the Smart Program workflow.
									</Typography>
								)}
							</Grid>
							{formData.smartProgram?.workflow && (
								<SavedSmartProgramDetails
									workflow={formData.smartProgram.workflow}
									fieldDefinitions={formData.smartProgram.fieldDefinitions}
									description={formData.smartProgram.description}
									generationPrompt={formData.smartProgram.generationPrompt}
									previewHtml={formData.smartProgram.previewHtml}
								/>
							)}
							<Grid item xs={12}>
								<FieldDefinitionsEditor
									fields={formData.smartProgram?.fieldDefinitions || []}
									onChange={(fields) =>
										setFormData({
											...formData,
											smartProgram: {
												...formData.smartProgram,
												fieldDefinitions: fields,
											},
										})
									}
								/>
							</Grid>
						</>
					)}

					{/* Treatment Config Templates (Weekly Calendar only) */}
					{(!formData.programType ||
						formData.programType === "WEEKLY_CALENDAR") && (
						<>
							{/* Treatment Config Templates */}
							<Grid item xs={12}>
								<Box
									sx={{
										border: "1px solid #e0e0e0",
										borderRadius: 1,
										p: 2,
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 2,
										}}
									>
										<Typography variant="subtitle1">
											Treatment Config Templates
										</Typography>
										<Button
											variant="outlined"
											size="small"
											onClick={() => {
												if (showTemplateForm) {
													// Cancel - close form and reset
													setShowTemplateForm(false);
													setEditingTemplateIndex(null);
													setTemplateData({
														name: "",
														default: false,
														segment: 0,
														range: "",
														duration: 0,
														periodLength: 1,
														weeklyVisitsPerPeriod: 0,
														cmVisitScheduleOptionIds: "",
														rewardEngineId: "",
														visitsPerPeriod: 0,
														minHoursBetweenVisits: 0,
														cmVisitSchedule: {
															cmVisitScheduleOptions: 0,
															cmVisitScheduleOptionsDays: "",
															cmVisitScheduleOptionsDaysDisplay: "",
															cutDays: [],
														},
														behavior: "",
														disenrollmentDaysThreshold: undefined,
														allowCustomProviderSite: false,
													});
												} else {
													// Open form in create mode
													setEditingTemplateIndex(null);
													setShowTemplateForm(true);
												}
											}}
										>
											{showTemplateForm ? "Cancel" : "Add Template"}
										</Button>
									</Box>

									{/* Show existing templates */}
									{formData.treatmentConfigOptions?.treatmentConfigTemplates &&
										formData.treatmentConfigOptions.treatmentConfigTemplates
											.length > 0 && (
											<Box sx={{ mb: 2 }}>
												<Typography
													variant="caption"
													display="block"
													sx={{ mb: 1 }}
												>
													Existing Templates:
												</Typography>
												<Box
													sx={{
														display: "flex",
														flexWrap: "wrap",
														gap: 1,
													}}
												>
													{formData.treatmentConfigOptions.treatmentConfigTemplates.map(
														(template, index) => (
															<Box
																key={index}
																sx={{
																	display: "flex",
																	alignItems: "center",
																	border: "1px solid #e0e0e0",
																	borderRadius: 1,
																	px: 1,
																	py: 0.5,
																}}
															>
																<Typography
																	variant="body2"
																	sx={{ mr: 1, fontWeight: 500 }}
																>
																	{template.name || `Template ${index + 1}`}
																</Typography>
																<IconButton
																	size="small"
																	onClick={() => editTemplate(index)}
																	title="Edit template"
																	sx={{ p: 0.5 }}
																>
																	<EditIcon fontSize="small" />
																</IconButton>
																<IconButton
																	size="small"
																	onClick={() => removeTemplate(index)}
																	title="Delete template"
																	sx={{ p: 0.5 }}
																	color="error"
																>
																	<DeleteIcon fontSize="small" />
																</IconButton>
															</Box>
														),
													)}
												</Box>
											</Box>
										)}

									{/* Template creation form */}
									{showTemplateForm && (
										<Card variant="outlined" sx={{ p: 2 }}>
											<Typography variant="subtitle2" sx={{ mb: 2 }}>
												{editingTemplateIndex !== null
													? "Edit Template"
													: "Create New Template"}
											</Typography>

											<Grid container spacing={2}>
												<Grid item xs={12}>
													{/* Use the reusable TreatmentConfigFields component */}
													<TreatmentConfigFields
														config={templateData}
														onChange={(updatedConfig) =>
															setTemplateData(updatedConfig)
														}
														options={{
															behaviors: events || [],
															durations:
																formData.treatmentConfigOptions?.duration?.map(
																	String,
																) || [],
															visitsPerPeriod:
																formData.treatmentConfigOptions?.visitsPerPeriod?.map(
																	String,
																) || [],
															minHoursBetweenVisits:
																formData.treatmentConfigOptions?.minHoursBetweenVisits?.map(
																	String,
																) || [],
															cmVisitScheduleOptions:
																formData.treatmentConfigOptions
																	?.cmVisitScheduleOptions || [],
															rewardEngineOptions:
																rewardEngines
																	?.filter((engine) => engine._id)
																	.map((engine) => ({
																		id: engine._id as string,
																		name: `${engine.eventType} - ${engine.event}`,
																	})) || [],
															cups: cupsData?.body || [],
														}}
														showDefaultCheckbox={true}
														showAllowCustomProviderSite={true}
													/>
												</Grid>

												{/* Additional Treatment Configs Section */}
												<Grid item xs={12}>
													<Box
														sx={{
															border: "1px solid #e0e0e0",
															borderRadius: 1,
															p: 2,
															mt: 2,
														}}
													>
														<Box
															sx={{
																display: "flex",
																justifyContent: "space-between",
																alignItems: "center",
																mb: 2,
															}}
														>
															<Typography variant="subtitle2">
																Additional Activities
															</Typography>
															<Typography
																variant="caption"
																color="text.secondary"
															>
																Add more activities beyond the preferred one
															</Typography>
														</Box>

														{/* List of Additional Configs */}
														{templateData.additionalTreatmentConfigs &&
															templateData.additionalTreatmentConfigs.length >
																0 && (
																<Box sx={{ mb: 2 }}>
																	{templateData.additionalTreatmentConfigs.map(
																		(config, index) => (
																			<Paper
																				key={index}
																				sx={{
																					p: 2,
																					mb: 1,
																					display: "flex",
																					justifyContent: "space-between",
																					alignItems: "center",
																				}}
																			>
																				<Box>
																					<Typography
																						variant="body2"
																						fontWeight="bold"
																					>
																						{config.name}
																					</Typography>
																					<Typography
																						variant="caption"
																						color="text.secondary"
																					>
																						Segment: {config.segment}, Duration:{" "}
																						{config.duration}, Visits/Period:{" "}
																						{config.visitsPerPeriod}
																					</Typography>
																				</Box>
																				<Box>
																					<IconButton
																						size="small"
																						onClick={() => {
																							// Remove this additional config
																							const updatedConfigs = [
																								...(templateData.additionalTreatmentConfigs ||
																									[]),
																							];
																							updatedConfigs.splice(index, 1);
																							setTemplateData({
																								...templateData,
																								additionalTreatmentConfigs:
																									updatedConfigs,
																							});
																						}}
																						color="error"
																					>
																						<DeleteIcon />
																					</IconButton>
																				</Box>
																			</Paper>
																		),
																	)}
																</Box>
															)}

														{/* Add Additional Config Form */}
														<Button
															variant="outlined"
															size="small"
															startIcon={<AddIcon />}
															onClick={() => {
																// Create a simplified config based on current template
																const newAdditionalConfig: TreatmentConfigTemplate =
																	{
																		name: `${templateData.name} - Additional Activity ${(templateData.additionalTreatmentConfigs?.length || 0) + 1}`,
																		default: false,
																		segment: templateData.segment,
																		range: templateData.range,
																		duration: templateData.duration,
																		periodLength: templateData.periodLength,
																		weeklyVisitsPerPeriod:
																			templateData.weeklyVisitsPerPeriod,
																		cmVisitScheduleOptionIds:
																			templateData.cmVisitScheduleOptionIds,
																		rewardEngineId: templateData.rewardEngineId,
																		visitsPerPeriod:
																			templateData.visitsPerPeriod,
																		minHoursBetweenVisits:
																			templateData.minHoursBetweenVisits,
																		cmVisitSchedule: {
																			...templateData.cmVisitSchedule,
																		},
																		behavior: "", // Empty behavior to be selected
																		disenrollmentDaysThreshold:
																			templateData.disenrollmentDaysThreshold,
																		allowCustomProviderSite:
																			templateData.allowCustomProviderSite,
																		additionalTreatmentConfigs: [], // Nested configs not supported
																	};

																setTemplateData({
																	...templateData,
																	additionalTreatmentConfigs: [
																		...(templateData.additionalTreatmentConfigs ||
																			[]),
																		newAdditionalConfig,
																	],
																});
															}}
														>
															Add Additional Activity
														</Button>

														{/* Edit forms for each additional config */}
														{templateData.additionalTreatmentConfigs &&
															templateData.additionalTreatmentConfigs.length >
																0 && (
																<Box sx={{ mt: 2 }}>
																	{templateData.additionalTreatmentConfigs.map(
																		(config, index) => {
																			const updateConfig = (
																				updates: Partial<TreatmentConfigTemplate>,
																			) => {
																				const updatedConfigs = [
																					...(templateData.additionalTreatmentConfigs ||
																						[]),
																				];
																				updatedConfigs[index] = {
																					...updatedConfigs[index],
																					...updates,
																				};
																				setTemplateData({
																					...templateData,
																					additionalTreatmentConfigs:
																						updatedConfigs,
																				});
																			};

																			return (
																				<Card
																					key={index}
																					variant="outlined"
																					sx={{
																						p: 2,
																						mb: 2,
																						bgcolor: "grey.50",
																					}}
																				>
																					<Box
																						sx={{
																							display: "flex",
																							justifyContent: "space-between",
																							alignItems: "center",
																							mb: 2,
																						}}
																					>
																						<Typography variant="subtitle2">
																							Additional Activity {index + 1}
																						</Typography>
																						<IconButton
																							size="small"
																							onClick={() => {
																								const updatedConfigs = [
																									...(templateData.additionalTreatmentConfigs ||
																										[]),
																								];
																								updatedConfigs.splice(index, 1);
																								setTemplateData({
																									...templateData,
																									additionalTreatmentConfigs:
																										updatedConfigs,
																								});
																							}}
																							color="error"
																						>
																							<DeleteIcon />
																						</IconButton>
																					</Box>

																					{/* Use the reusable TreatmentConfigFields component */}
																					<TreatmentConfigFields
																						config={config}
																						onChange={(updatedConfig) =>
																							updateConfig(updatedConfig)
																						}
																						options={{
																							behaviors: events || [],
																							durations:
																								formData.treatmentConfigOptions?.duration?.map(
																									String,
																								) || [],
																							visitsPerPeriod:
																								formData.treatmentConfigOptions?.visitsPerPeriod?.map(
																									String,
																								) || [],
																							minHoursBetweenVisits:
																								formData.treatmentConfigOptions?.minHoursBetweenVisits?.map(
																									String,
																								) || [],
																							cmVisitScheduleOptions:
																								formData.treatmentConfigOptions
																									?.cmVisitScheduleOptions ||
																								[],
																							rewardEngineOptions:
																								rewardEngines
																									?.filter(
																										(engine) => engine._id,
																									)
																									.map((engine) => ({
																										id: engine._id as string,
																										name: `${engine.eventType} - ${engine.event}`,
																									})) || [],
																							cups: cupsData?.body || [],
																						}}
																						showDefaultCheckbox={false}
																						showAllowCustomProviderSite={true}
																						isAdditionalConfig={true}
																					/>
																				</Card>
																			);
																		},
																	)}
																</Box>
															)}
													</Box>
												</Grid>

												<Grid item xs={12}>
													<Button
														variant="contained"
														onClick={addTemplate}
														disabled={!templateData.name.trim()}
													>
														{editingTemplateIndex !== null
															? "Update Template"
															: "Add Template"}
													</Button>
												</Grid>
											</Grid>
										</Card>
									)}
								</Box>
							</Grid>

							{/* CM Visit Schedule Options */}
							<Grid item xs={12}>
								<Box
									sx={{
										border: "1px solid #e0e0e0",
										borderRadius: 1,
										p: 2,
									}}
								>
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											mb: 2,
										}}
									>
										<Typography variant="subtitle1">
											CM Visit Schedule Options
										</Typography>
										<Button
											variant="outlined"
											size="small"
											onClick={() =>
												setShowCMVisitScheduleForm(!showCMVisitScheduleForm)
											}
										>
											{showCMVisitScheduleForm
												? "Cancel"
												: "Add Schedule Option"}
										</Button>
									</Box>

									{formData.treatmentConfigOptions?.cmVisitScheduleOptions &&
										formData.treatmentConfigOptions.cmVisitScheduleOptions
											.length > 0 && (
											<Box sx={{ mb: 2 }}>
												<Typography
													variant="caption"
													display="block"
													sx={{ mb: 1 }}
												>
													Existing Schedule Options:
												</Typography>
												{formData.treatmentConfigOptions.cmVisitScheduleOptions.map(
													(option, index) => (
														<Chip
															key={index}
															label={
																option.cmVisitScheduleOptionsDaysDisplay ||
																`Option ${index + 1}`
															}
															onDelete={() =>
																removeCMVisitScheduleOption(index)
															}
															deleteIcon={<DeleteIcon />}
															sx={{ mr: 1, mb: 1 }}
														/>
													),
												)}
											</Box>
										)}

									{showCMVisitScheduleForm && (
										<Card variant="outlined" sx={{ p: 2 }}>
											<Typography variant="subtitle2" sx={{ mb: 2 }}>
												Create New CM Visit Schedule Option
											</Typography>
											<Grid container spacing={2}>
												{/* Preset Schedule Options */}
												<Grid item xs={12}>
													<FormControl fullWidth size="small">
														<InputLabel>Quick Schedule Presets</InputLabel>
														<Select
															value=""
															onChange={(e) =>
																handlePresetSchedule(Number(e.target.value))
															}
															label="Quick Schedule Presets"
														>
															<MenuItem value={-1}>
																<em>Clear Selection</em>
															</MenuItem>
															{COMMON_VISIT_SCHEDULES.map((schedule, index) => (
																<MenuItem key={index} value={index}>
																	{schedule.name}
																</MenuItem>
															))}
														</Select>
													</FormControl>
												</Grid>

												{/* Manual Weekday Selection */}
												<Grid item xs={12}>
													<Typography
														variant="caption"
														display="block"
														sx={{ mb: 1 }}
													>
														Select Days of Week:
													</Typography>
													<Box
														sx={{
															display: "flex",
															flexWrap: "wrap",
															gap: 1,
														}}
													>
														{WEEKDAYS.map((weekday) => (
															<Chip
																key={weekday.value}
																label={weekday.shortName}
																onClick={() =>
																	handleWeekdayToggle(weekday.value)
																}
																color={
																	cmVisitScheduleData.selectedWeekdays.includes(
																		weekday.value,
																	)
																		? "primary"
																		: "default"
																}
																variant={
																	cmVisitScheduleData.selectedWeekdays.includes(
																		weekday.value,
																	)
																		? "filled"
																		: "outlined"
																}
																sx={{ cursor: "pointer" }}
															/>
														))}
													</Box>
												</Grid>

												{/* Cut Days Configuration */}
												<Grid item xs={12}>
													<Typography
														variant="caption"
														display="block"
														sx={{ mb: 2 }}
													>
														Configure Cut Days Schedule (running day, start cut
														day, end cut day):
													</Typography>

													{cmVisitScheduleData.cutDays.length > 0 && (
														<Box sx={{ mb: 2 }}>
															{cmVisitScheduleData.cutDays.map(
																(cutDay, index) => (
																	<Box
																		key={index}
																		sx={{
																			display: "flex",
																			alignItems: "center",
																			gap: 2,
																			mb: 1,
																			p: 1,
																			border: "1px solid #ddd",
																			borderRadius: 1,
																		}}
																	>
																		<Typography variant="body2">
																			Pattern {index + 1}:
																		</Typography>
																		<Typography variant="body2">
																			Running: {cutDay.runningday}
																		</Typography>
																		<Typography variant="body2">
																			Start Cut: {cutDay.startcutday}
																		</Typography>
																		<Typography variant="body2">
																			End Cut: {cutDay.endcutday}
																		</Typography>
																		<Button
																			size="small"
																			color="error"
																			onClick={() => {
																				const newCutDays =
																					cmVisitScheduleData.cutDays.filter(
																						(_, i) => i !== index,
																					);
																				setCMVisitScheduleData({
																					...cmVisitScheduleData,
																					cutDays: newCutDays,
																				});
																			}}
																		>
																			Remove
																		</Button>
																	</Box>
																),
															)}
														</Box>
													)}

													{!showCutDayForm ? (
														<Button
															variant="outlined"
															onClick={() => setShowCutDayForm(true)}
															sx={{ mb: 2 }}
														>
															Add Cut Day Pattern
														</Button>
													) : (
														<Paper sx={{ p: 2, mb: 2 }}>
															<Typography variant="subtitle2" sx={{ mb: 2 }}>
																Add New Cut Day Pattern
															</Typography>
															<Grid container spacing={2}>
																<Grid item xs={12} md={4}>
																	<FormControl fullWidth size="small">
																		<InputLabel>Running Day</InputLabel>
																		<Select
																			value={newCutDayPattern.runningday}
																			label="Running Day"
																			onChange={(e) =>
																				setNewCutDayPattern({
																					...newCutDayPattern,
																					runningday: e.target.value,
																				})
																			}
																		>
																			{WEEKDAYS.map((weekday) => (
																				<MenuItem
																					key={weekday.shortName}
																					value={weekday.shortName}
																				>
																					{weekday.fullName}
																				</MenuItem>
																			))}
																		</Select>
																	</FormControl>
																</Grid>
																<Grid item xs={12} md={4}>
																	<FormControl fullWidth size="small">
																		<InputLabel>Start Cut Day</InputLabel>
																		<Select
																			value={newCutDayPattern.startcutday}
																			label="Start Cut Day"
																			onChange={(e) =>
																				setNewCutDayPattern({
																					...newCutDayPattern,
																					startcutday: e.target.value,
																				})
																			}
																		>
																			{WEEKDAYS.map((weekday) => (
																				<MenuItem
																					key={weekday.shortName}
																					value={weekday.shortName}
																				>
																					{weekday.fullName}
																				</MenuItem>
																			))}
																		</Select>
																	</FormControl>
																</Grid>
																<Grid item xs={12} md={4}>
																	<FormControl fullWidth size="small">
																		<InputLabel>End Cut Day</InputLabel>
																		<Select
																			value={newCutDayPattern.endcutday}
																			label="End Cut Day"
																			onChange={(e) =>
																				setNewCutDayPattern({
																					...newCutDayPattern,
																					endcutday: e.target.value,
																				})
																			}
																		>
																			{WEEKDAYS.map((weekday) => (
																				<MenuItem
																					key={weekday.shortName}
																					value={weekday.shortName}
																				>
																					{weekday.fullName}
																				</MenuItem>
																			))}
																		</Select>
																	</FormControl>
																</Grid>
																<Grid item xs={12}>
																	<Box sx={{ display: "flex", gap: 1 }}>
																		<Button
																			variant="contained"
																			size="small"
																			onClick={() => {
																				setCMVisitScheduleData({
																					...cmVisitScheduleData,
																					cutDays: [
																						...cmVisitScheduleData.cutDays,
																						newCutDayPattern,
																					],
																				});
																				setNewCutDayPattern({
																					runningday: "Mon",
																					startcutday: "Mon",
																					endcutday: "Mon",
																				});
																				setShowCutDayForm(false);
																			}}
																		>
																			Add Pattern
																		</Button>
																		<Button
																			variant="outlined"
																			size="small"
																			onClick={() => {
																				setShowCutDayForm(false);
																				setNewCutDayPattern({
																					runningday: "Mon",
																					startcutday: "Mon",
																					endcutday: "Mon",
																				});
																			}}
																		>
																			Cancel
																		</Button>
																	</Box>
																</Grid>
															</Grid>
														</Paper>
													)}

													<Typography
														variant="caption"
														display="block"
														sx={{ mb: 1 }}
													>
														Quick select cut days (same day for
														running/start/end):
													</Typography>
													<Box
														sx={{
															display: "flex",
															flexWrap: "wrap",
															gap: 1,
														}}
													>
														{WEEKDAYS.map((weekday) => (
															<Chip
																key={`cut-${weekday.value}`}
																label={weekday.shortName}
																onClick={() =>
																	handleCutDayToggle(weekday.value)
																}
																color={
																	cmVisitScheduleData.selectedCutDays.includes(
																		weekday.value,
																	)
																		? "error"
																		: "default"
																}
																variant={
																	cmVisitScheduleData.selectedCutDays.includes(
																		weekday.value,
																	)
																		? "filled"
																		: "outlined"
																}
																sx={{ cursor: "pointer" }}
															/>
														))}
													</Box>
												</Grid>

												{/* Generated Fields */}
												<Grid item xs={12} md={4}>
													<TextField
														fullWidth
														label="Schedule Options (Number)"
														type="number"
														value={cmVisitScheduleData.cmVisitScheduleOptions}
														onChange={(e) =>
															setCMVisitScheduleData({
																...cmVisitScheduleData,
																cmVisitScheduleOptions: Number(e.target.value),
															})
														}
														size="small"
														helperText="Number of schedule options"
													/>
												</Grid>
												<Grid item xs={12} md={4}>
													<TextField
														fullWidth
														label="Schedule Days (Generated)"
														value={
															cmVisitScheduleData.cmVisitScheduleOptionsDays
														}
														InputProps={{
															readOnly: true,
														}}
														size="small"
														helperText="Auto-generated from weekday selection"
													/>
												</Grid>
												<Grid item xs={12} md={4}>
													<TextField
														fullWidth
														label="Display Name (Generated)"
														value={
															cmVisitScheduleData.cmVisitScheduleOptionsDaysDisplay
														}
														onChange={(e) =>
															setCMVisitScheduleData({
																...cmVisitScheduleData,
																cmVisitScheduleOptionsDaysDisplay:
																	e.target.value,
															})
														}
														size="small"
														helperText="Edit the display name if needed"
													/>
												</Grid>

												{/* Preview */}
												{cmVisitScheduleData.selectedWeekdays.length > 0 && (
													<Grid item xs={12}>
														<Paper sx={{ p: 2, bgcolor: "grey.50" }}>
															<Typography variant="caption" display="block">
																Preview:
															</Typography>
															<Typography variant="body2">
																<strong>Visit Days:</strong>{" "}
																{cmVisitScheduleData.cmVisitScheduleOptionsDays}
															</Typography>
															<Typography variant="body2">
																<strong>Display:</strong>{" "}
																{
																	cmVisitScheduleData.cmVisitScheduleOptionsDaysDisplay
																}
															</Typography>
															{cmVisitScheduleData.selectedCutDays.length >
																0 && (
																<Typography variant="body2">
																	<strong>Cut Days:</strong>{" "}
																	{formatWeekdaysDisplay(
																		cmVisitScheduleData.selectedCutDays,
																		"short",
																	)}
																</Typography>
															)}
														</Paper>
													</Grid>
												)}

												<Grid item xs={12}>
													<Button
														variant="contained"
														onClick={addCMVisitScheduleOption}
														disabled={
															cmVisitScheduleData.selectedWeekdays.length ===
																0 ||
															!cmVisitScheduleData.cmVisitScheduleOptionsDaysDisplay.trim()
														}
													>
														Add Schedule Option
													</Button>
												</Grid>
											</Grid>
										</Card>
									)}
								</Box>
							</Grid>

							{/* Visits Per Period */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Visits Per Period
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
									<TextField
										size="small"
										value={newVisitPeriod}
										onChange={(e) => setNewVisitPeriod(e.target.value)}
										placeholder="e.g., weekly, monthly"
									/>
									<IconButton
										onClick={() =>
											addArrayItem(
												"visitsPerPeriod",
												newVisitPeriod,
												setNewVisitPeriod,
											)
										}
									>
										<AddIcon />
									</IconButton>
								</Box>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.treatmentConfigOptions?.visitsPerPeriod?.map(
										(item, index) => (
											<Chip
												key={index}
												label={item}
												onDelete={() =>
													removeArrayItem("visitsPerPeriod", index)
												}
												deleteIcon={<DeleteIcon />}
											/>
										),
									)}
								</Box>
							</Grid>

							{/* Min Hours Between Visits */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Min Hours Between Visits
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
									<TextField
										size="small"
										value={newMinHours}
										onChange={(e) => setNewMinHours(e.target.value)}
										placeholder="e.g., 24, 48"
									/>
									<IconButton
										onClick={() =>
											addArrayItem(
												"minHoursBetweenVisits",
												newMinHours,
												setNewMinHours,
											)
										}
									>
										<AddIcon />
									</IconButton>
								</Box>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.treatmentConfigOptions?.minHoursBetweenVisits?.map(
										(item, index) => (
											<Chip
												key={index}
												label={item}
												onDelete={() =>
													removeArrayItem("minHoursBetweenVisits", index)
												}
												deleteIcon={<DeleteIcon />}
											/>
										),
									)}
								</Box>
							</Grid>

							{/* Duration */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Duration
								</Typography>
								<Box sx={{ display: "flex", gap: 1, mb: 1 }}>
									<TextField
										size="small"
										value={newDuration}
										onChange={(e) => setNewDuration(e.target.value)}
										placeholder="e.g., 30 days, 60 days"
									/>
									<IconButton
										onClick={() =>
											addArrayItem("duration", newDuration, setNewDuration)
										}
									>
										<AddIcon />
									</IconButton>
								</Box>
								<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
									{formData.treatmentConfigOptions?.duration?.map(
										(item, index) => (
											<Chip
												key={index}
												label={item}
												onDelete={() => removeArrayItem("duration", index)}
												deleteIcon={<DeleteIcon />}
											/>
										),
									)}
								</Box>
							</Grid>

							{/* Behavior */}
							<Grid item xs={12}>
								<Typography variant="subtitle1" sx={{ mb: 1 }}>
									Behavior (Events)
								</Typography>
								<BehaviorSelector
									label="Treatment Plan Behaviors"
									placeholder="Choose events that represent behaviors"
									helperText="Select from available events - each event represents a behavior type that will be available in treatment configurations"
									value={formData.treatmentConfigOptions?.behavior || []}
									onChange={(behaviorIds) => {
										if (formData.treatmentConfigOptions) {
											setFormData({
												...formData,
												treatmentConfigOptions: {
													...formData.treatmentConfigOptions,
													behavior: behaviorIds,
												},
											});
										}
									}}
									events={events}
									loading={eventsLoading}
								/>
							</Grid>

							{/* Custom Plan Allowed */}
							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.customPlanAllowed || false}
											onChange={(e) =>
												setFormData({
													...formData,
													customPlanAllowed: e.target.checked,
												})
											}
										/>
									}
									label="Allow Custom Plans"
									sx={{ mt: 1 }}
								/>
								<Typography
									variant="caption"
									display="block"
									color="text.secondary"
								>
									When enabled, users can create custom treatment configurations
									for this plan
								</Typography>
							</Grid>

							{/* SIMS-8910 (FR-1/FR-2): missed-entry generation.
							    Rendered inside the WEEKLY_CALENDAR branch, which is what keeps it
							    off Smart Program plans (AC3) — those render their own section and
							    never reach this block. */}
							<Grid item xs={12}>
								<FormControlLabel
									control={
										<Checkbox
											checked={formData.disableMissedEntriesGeneration ?? false}
											onChange={(e) =>
												setFormData({
													...formData,
													disableMissedEntriesGeneration: e.target.checked,
												})
											}
											inputProps={{
												"aria-describedby":
													"disable-missed-entries-generation-help",
											}}
										/>
									}
									label="Disable Missed Entry Generation"
									sx={{ mt: 1 }}
								/>
								<Typography
									id="disable-missed-entries-generation-help"
									variant="caption"
									display="block"
									color="text.secondary"
								>
									When enabled, the nightly process stops creating missed
									entries for participants on this plan, including those already
									enrolled. Missed entries recorded before this was turned on
									stay in place and still require a result.
								</Typography>
							</Grid>

							{/* SIMS-7151/7159: plan-level segment editor (FR-3, FR-4, FR-6) */}
							<Grid item xs={12}>
								<SegmentEditor
									segments={formData.segments || []}
									onChange={(segments: TreatmentSegment[]) =>
										setFormData({ ...formData, segments })
									}
									rewardEngineOptions={rewardEngineOptions}
									behaviors={events || []}
									cmVisitScheduleOptions={
										formData.treatmentConfigOptions?.cmVisitScheduleOptions ||
										[]
									}
									templateDefaults={segmentTemplateDefaults}
								/>
							</Grid>
						</>
					)}

					<Grid item xs={12}>
						<Box sx={{ display: "flex", gap: 2 }}>
							<Button
								variant="contained"
								color="primary"
								onClick={handleSubmit}
								disabled={createMutation.isPending || updateMutation.isPending}
							>
								{createMutation.isPending || updateMutation.isPending
									? "Saving..."
									: isEditMode
										? "Update Treatment Plan"
										: "Create Treatment Plan"}
							</Button>
							<Button variant="outlined" onClick={onClose}>
								Cancel
							</Button>
						</Box>
					</Grid>
				</Grid>
			</CardContent>
		</Card>
	);
};

export default TreatmentPlanForm;
